<template>
    <div>
        <div v-if="!isEmbedded" class="uk-padding logo-struct-transfer">
            <div class="uk-grid-collapse uk-flex-center" uk-grid>
                <div class="uk-width-expand logo-struct-transfer-element" />
                <div class="logo-box uk-width-auto">
                    {{ getInfos.transfer.name }}
                    <span class="uk-margin-small-right" />
                    <img
                        :src="apiUrl + '/logos/' + getInfos.logo"
                        class="logo"
                        alt="Logo" />
                </div>
            </div>
        </div>

        <LoadingStruct
            :class="isEmbedded ? '' : 'transfer-struct'"
            is-dark
            :is-loading="!getInfos || loading"
            :error="error">
            <countdown
                class="uk-hidden"
                :end-time="Date.now() + 60 * 60 * 1000"
                @finish="reload" />

            <Card
                is-round
                v-if="!getInfos.transfer.channelEnabled"
                :title="$t('transfer.does.not.exist')"
                is-dark
                mode="default"
                :class="
                    isEmbedded
                        ? 'uk-height-viewport uk-text-left'
                        : 'uk-text-left'
                "
                is-centered>
                <InfoBox class="uk-text-left" mode="danger">
                    <Translate
                        translation-key="transfer.does.not.exist.message" />
                </InfoBox>
            </Card>

            <Card
                v-else
                is-round
                is-dark
                is-without-body
                :class="
                    isEmbedded
                        ? 'uk-height-viewport card-struct'
                        : 'card-struct'
                "
                mode="default">
                <div
                    class="transfer-content-struct uk-padding-small uk-animation-fade"
                    v-if="showStepEmail">
                    <div class="uk-text-center transfer-name">
                        <Translate translation-key="email.address.confirm" />
                    </div>

                    <div class="uk-margin-medium-top" />
                    <Description>
                        <Translate
                            translation-key="email.address.confirm.message" />
                    </Description>

                    <span
                        v-for="(inputField, key) in inputFieldsCode"
                        :key="inputField.name">
                        <TextField
                            is-round
                            v-model="inputField.value"
                            :autocomplete="inputField.autocomplete"
                            :autofocus="inputField.autofocus"
                            :field-key="key"
                            :hasPasswordMeter="inputField.hasPasswordMeter"
                            :icon="inputField.icon"
                            :is-optional="inputField.optional"
                            :label="inputField.label"
                            :name="inputField.name"
                            :options="inputField.options"
                            :rules="inputField.rules"
                            :steps="inputField.steps"
                            :type="inputField.type"
                            :on-enter="actionComplete"
                            :disabled="completeSuccess"
                            :validate="validationTriggerCode"
                            @setValid="setValidCode" />
                    </span>

                    <InfoBox
                        v-if="anyUploadInProgress"
                        mode="warning"
                        class="uk-margin-top">
                        <Translate translation-key="files.upload.in.progress" />

                        <div class="uk-margin-top" />
                        <progress
                            :value="getUnifiedUploadPercentage"
                            class="uk-progress"
                            max="100" />
                        <div class="upload-status uk-text-center">
                            {{
                                $t('upload.status', {
                                    x: getUnifiedUploadPercentage,
                                })
                            }}
                        </div>
                    </InfoBox>

                    <div class="uk-margin-top" />
                    <div class="uk-grid-small" uk-grid>
                        <div class="uk-width-auto">
                            <Tooltip :text="$t('back')">
                                <Button
                                    :is-disabled="completeSuccess"
                                    has-action
                                    :action="actionReturn"
                                    is-round
                                    is-large
                                    mode="default">
                                    <Icon
                                        icon="chevron-left"
                                        :ratio="1.4" /> </Button
                            ></Tooltip>
                        </div>
                        <div class="uk-width-expand">
                            <Button
                                :is-loading="completeLoading"
                                :is-disabled="
                                    completeSuccess || anyUploadInProgress
                                "
                                has-action
                                :action="actionComplete"
                                is-max
                                is-round
                                is-large
                                is-min-width
                                mode="success-alt">
                                <Icon icon="check" :ratio="1.4" />
                                <Translate translation-key="confirm" />
                            </Button>
                        </div>
                    </div>

                    <div v-if="completeErr" class="uk-margin-medium-bottom" />
                    <ErrorMessage
                        :custom-errors="[
                            {code: 403, message: $t('code.invalid')},
                        ]"
                        :error="completeErr" />

                    <div
                        v-if="completeSuccess"
                        class="uk-margin-medium-bottom" />
                    <SuccessMessage
                        :message="$t('files.upload.complete')"
                        :success="completeSuccess" />

                    <div class="uk-margin-medium-top" v-if="completeSuccess">
                        <Button
                            has-action
                            :action="reload"
                            is-max
                            is-round
                            is-min-width
                            mode="default">
                            <Translate translation-key="upload.more.files" />
                        </Button>
                    </div>

                    <div class="uk-margin-small-top" />
                </div>

                <div v-else class="transfer-content-struct">
                    <div
                        :class="
                            isEmbedded
                                ? 'uk-card-body'
                                : 'uk-card-body uk-panel-scrollable transfer-content-struct-panel'
                        ">
                        <LabelWithError
                            id="files-label"
                            :error="filesError"
                            :label="$t('files') + '*'" />

                        <div v-if="computedSizeExceeded">
                            <InfoBox mode="warn">
                                <Translate
                                    translation-key="upload.max.size.reached"
                                    :parameter="{
                                        x: getParsedBytes(
                                            getInfos.transfer.channelFreeBytes
                                        ),
                                    }" />
                            </InfoBox>
                        </div>
                        <div v-else>
                            <div class="uk-hidden" uk-form-custom>
                                <input
                                    :id="inputFormIDFiles"
                                    :ref="inputFormIDFiles"
                                    type="file"
                                    multiple
                                    v-on:change="handleDropFiles()"
                                    v-on:click="
                                        $refs[inputFormIDFiles].value = null
                                    " />

                                <input
                                    :id="inputFormIDFolders"
                                    :ref="inputFormIDFolders"
                                    type="file"
                                    webkitdirectory
                                    multiple
                                    v-on:change="handleDropFolders()"
                                    v-on:click="
                                        $refs[inputFormIDFolders].value = null
                                    " />
                            </div>

                            <div
                                class="uk-grid-small uk-child-width-expand@s uk-child-width-1-1"
                                uk-grid>
                                <div>
                                    <div
                                        :id="formFilesID"
                                        class="js-upload uk-placeholder uk-placeholder-hover uk-text-center cursor-pointer uk-padding-small"
                                        :class="{dragover: isDraggingFiles}"
                                        @dragover.prevent="
                                            isDraggingFiles = true
                                        "
                                        @dragleave="isDraggingFiles = false"
                                        @drop.prevent="onDropFiles"
                                        v-on:click="actionTriggerInputFiles()">
                                        <div>
                                            <Icon
                                                :ratio="1.5"
                                                class="upload-icon"
                                                icon="file-text" />
                                        </div>

                                        <span class="uk-text-middle">{{
                                            $t('files')
                                        }}</span>
                                    </div>
                                </div>
                                <div>
                                    <div
                                        :id="formFoldersID"
                                        class="js-upload uk-placeholder uk-placeholder-hover uk-text-center cursor-pointer uk-padding-small"
                                        :class="{dragover: isDraggingFolders}"
                                        @dragover.prevent="
                                            isDraggingFolders = true
                                        "
                                        @dragleave="isDraggingFolders = false"
                                        @drop.prevent="onDropFolders"
                                        v-on:click="
                                            actionTriggerInputFolders()
                                        ">
                                        <div>
                                            <Icon
                                                :ratio="1.5"
                                                class="upload-icon"
                                                icon="folder" />
                                        </div>

                                        <span class="uk-text-middle">{{
                                            $t('folders')
                                        }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="uk-margin-bottom" />

                        <div
                            v-if="files && files.length > 0"
                            class="uk-margin-bottom">
                            <div
                                class="uk-grid-small uk-child-width-1-1"
                                uk-grid>
                                <div
                                    v-for="(file, index) in files"
                                    :key="'file-' + index">
                                    <Card
                                        body-class="uk-padding-small"
                                        is-round
                                        is-dark
                                        mode="default">
                                        <div
                                            class="uk-grid uk-grid-collapse"
                                            uk-grid>
                                            <div
                                                class="uk-width-expand uk-flex-middle uk-flex">
                                                <div class="file-path">
                                                    <span
                                                        :uk-tooltip="
                                                            'title:' + file.path
                                                        ">
                                                        {{ file.path }}
                                                    </span>
                                                </div>
                                            </div>
                                            <div class="uk-width-auto">
                                                <span
                                                    class="uk-margin-small-right" />
                                                <span
                                                    :uk-tooltip="
                                                        $t('x.delete', {
                                                            x: $t('file'),
                                                        })
                                                    "
                                                    v-on:click="
                                                        removeFile(index, file)
                                                    ">
                                                    <Icon
                                                        class="cursor-pointer"
                                                        icon="trash" />
                                                </span>
                                            </div>
                                        </div>
                                    </Card>
                                </div>
                            </div>
                        </div>

                        <span
                            v-for="(inputField, key) in inputFields"
                            :key="inputField.name">
                            <TextField
                                is-round
                                v-model="inputField.value"
                                :autocomplete="inputField.autocomplete"
                                :autofocus="inputField.autofocus"
                                :field-key="key"
                                :hasPasswordMeter="inputField.hasPasswordMeter"
                                :icon="inputField.icon"
                                :is-optional="inputField.optional"
                                :label="inputField.label"
                                :name="inputField.name"
                                :options="inputField.options"
                                :rules="inputField.rules"
                                :steps="inputField.steps"
                                :type="inputField.type"
                                :on-enter="actionContinue"
                                :validate="validationTrigger"
                                @setValid="setValid" />

                            <div class="uk-margin-bottom" />
                        </span>
                    </div>

                    <div class="uk-card-footer uk-padding-remove">
                        <div class="uk-margin-top" />
                        <Button
                            :is-loading="continueLoading"
                            has-action
                            :action="actionContinue"
                            is-max
                            is-round
                            is-large
                            is-min-width
                            mode="success-alt">
                            <Icon icon="cloud-upload" :ratio="1.4" />
                            <span v-if="files.length > 0">
                                {{ files.length }}
                                {{
                                    files.length === 1
                                        ? $t('file')
                                        : $t('files')
                                }}
                            </span>
                        </Button>

                        <div
                            v-if="continueErr"
                            class="uk-margin-medium-bottom" />
                        <ErrorMessage :error="continueErr" />

                        <div
                            class="uk-margin-small-top transfer-size uk-text-center">
                            {{
                                $t('transfer.size') +
                                ': ' +
                                getParsedBytes(computedTotalFileSize)
                            }}

                            <div class="uk-text-small">
                                <Translate translation-key="max" />
                                {{
                                    getParsedBytes(
                                        getInfos.transfer.channelFreeBytes
                                    )
                                }}
                            </div>
                        </div>

                        <div class="uk-margin-small-top" />
                    </div>
                </div>
            </Card>
        </LoadingStruct>
    </div>
</template>

<script>
import LoadingStruct from '@/views/LoadingStruct';
import Card from '@/components/generic/Card';
import axios from 'axios';
import Translate from '../components/Translate.vue';
import Button from '../components/generic/Button.vue';
import InfoBox from '../components/generic/InfoBox.vue';
import Icon from '@/components/generic/Icon.vue';
import LabelWithError from '@/components/generic/LabelWithError.vue';
import TextField from '@/components/generic/TextField.vue';
import Description from '@/components/Description.vue';
import Tooltip from '@/components/generic/Tooltip.vue';
import SuccessMessage from '@/components/generic/SuccessMessage.vue';
import ErrorMessage from '@/components/generic/ErrorMessage.vue';
import UIkit from 'uikit';

export default {
    name: 'TransferChannel',
    components: {
        ErrorMessage,
        SuccessMessage,
        Tooltip,
        Description,
        TextField,
        LabelWithError,
        Icon,
        InfoBox,
        Button,
        Translate,
        Card,
        LoadingStruct,
    },
    data() {
        return {
            loading: true,
            error: null,
            token: '',

            inputFormIDFiles: 'transferfilesinputFiles',
            inputFormIDFolders: 'transferfilesinputFolders',

            formFilesID: 'transfer-channel-files-upload',
            formFoldersID: 'transfer-channel-folders-upload',

            files: [],
            filesError: null,

            isDraggingFiles: false,
            isDraggingFolders: false,

            showStepEmail: false,

            completeErr: null,
            completeSuccess: false,
            completeLoading: false,

            continueLoading: false,
            continueErr: null,

            validationTrigger: 0,
            inputFields: [
                {
                    value: '',
                    valid: false,

                    name: 'email',
                    label: 'emailAddress',
                    icon: 'mail',
                    rules: 'required|emailV2',
                },
                {
                    value: '',
                    valid: false,

                    name: 'name',
                    label: 'name',
                    icon: 'user',
                    rules: 'required|max:320',
                },
                {
                    value: '',
                    valid: false,

                    name: 'title',
                    label: 'title',
                    icon: 'hashtag',
                    rules: 'required|max:320',
                },
            ],

            validationTriggerCode: 0,
            inputFieldsCode: [
                {
                    value: '',
                    valid: false,

                    name: 'code',
                    label: 'code',
                    icon: 'code',
                    rules: 'required|min:8|max:8',
                },
            ],
        };
    },
    created() {
        this.getTransferToken();
    },
    computed: {
        computedTotalFileSize() {
            let totalSize = 0;

            for (const file of this.files) {
                totalSize += file.file.size;
            }

            return totalSize;
        },

        isEmbedded() {
            const embedded = this.$route.query.embedded;
            return embedded === 'true';
        },
        computedSizeExceeded() {
            return (
                this.computedTotalFileSize >=
                this.getInfos.transfer.channelFreeBytes
            );
        },
        anyUploadInProgress() {
            for (let i = 0, j = this.files.length; i < j; i++) {
                if (this.files[i].uploadPercentage !== 100) {
                    return true;
                }
            }

            return false;
        },
        getUnifiedUploadPercentage() {
            let pictures = this.files;
            let total = 0;
            for (let i = 0, j = pictures.length; i < j; i++) {
                total += pictures[i].uploadPercentage;
            }

            let totalPercentage = total / pictures.length;
            return (Math.round(totalPercentage * 100) / 100).toFixed(0);
        },
    },
    methods: {
        onDropFiles(e) {
            this.isDraggingFiles = false;
            this.onDropFolders(e);
        },
        async onDropFolders(e) {
            this.isDraggingFolders = false;

            if (!e.dataTransfer?.items?.length) return;

            const files = [];

            for (const item of e.dataTransfer.items) {
                const entry = item.webkitGetAsEntry();
                if (entry) {
                    await this.traverseEntry(entry, files);
                }
            }

            this.handleDrop(files);
        },
        async traverseEntry(entry, files, path = '') {
            if (entry.isFile) {
                await new Promise((resolve) => {
                    entry.file((file) => {
                        // Attach relative path for later use
                        file._relativePath = path + file.name;
                        files.push(file);
                        resolve();
                    });
                });
            } else if (entry.isDirectory) {
                const reader = entry.createReader();
                const entries = await new Promise((resolve) =>
                    reader.readEntries(resolve)
                );

                for (const child of entries) {
                    await this.traverseEntry(
                        child,
                        files,
                        `${path}${entry.name}/`
                    );
                }
            }
        },
        uploadQueue() {
            let self = this;
            this.intervalUpload = setInterval(() => {
                for (let i = 0, j = self.files.length; i < j; i++) {
                    if (self.files[i].inProgress) {
                        return;
                    }
                }

                for (let i = 0, j = self.files.length; i < j; i++) {
                    if (self.files[i].isDone) {
                        continue;
                    }

                    if (!self.files[i].file) {
                        continue;
                    }

                    self.files[i].inProgress = true;
                    self.actionUploadFile(self.files[i]);

                    return;
                }
            }, 100);
        },
        actionUploadFile(fileStruct) {
            let self = this;

            let formData = new FormData();
            formData.append('file', fileStruct.file, fileStruct.file.name);
            formData.append('path', fileStruct.path);

            const config = {
                onUploadProgress: function (progressEvent) {
                    fileStruct.uploadPercentage = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                },
            };

            let http = axios.create();
            http.post(
                this.apiUrl + '/swift/requests/' + this.token + '/files',
                formData,
                config
            )
                .then(() => {
                    fileStruct.uploadPercentage = 100;

                    fileStruct.inProgress = false;
                    fileStruct.isDone = true;
                })
                .catch((e) => {
                    self.checkGenericError(e);
                    fileStruct.uploadPercentage = 0;
                    fileStruct.error = e;
                    fileStruct.inProgress = false;
                    fileStruct.isDone = true;
                });
        },
        removeFile(key, fileStruct) {
            let self = this;

            let formData = new FormData();
            formData.append('path', fileStruct.path);

            let http = axios.create();
            http.post(
                this.apiUrl + '/swift/requests/' + this.token + '/remove-files',
                formData
            )
                .then(() => {
                    this.files.splice(key, 1);
                })
                .catch((e) => {
                    self.checkGenericError(e);

                    UIkit.notification({
                        message: this.$t('unexpected.error'),
                        status: 'error',
                        pos: 'top-center',
                        timeout: this.notificationTimeout,
                    });
                });
        },
        handleDropFiles() {
            const input = this.$refs[this.inputFormIDFiles];
            this.handleDrop(input.files);
        },
        handleDropFolders() {
            const input = this.$refs[this.inputFormIDFolders];
            this.handleDrop(input.files);
        },
        handleDrop(rawFiles) {
            const files = Array.from(rawFiles);

            let totalSize = this.computedTotalFileSize;

            for (const file of files) {
                totalSize += file.size;
            }

            if (totalSize > this.getInfos.transfer.channelFreeBytes) {
                this.filesError = this.$t('name.can.be.maximum.size', {
                    size: this.getParsedBytes(
                        this.getInfos.transfer.channelFreeBytes
                    ),
                    name: this.$t('files'),
                });
                return;
            }

            const normalizedFiles = files.map((file) => ({
                file,
                path:
                    file.webkitRelativePath || file._relativePath || file.name,
                uploadPercentage: 0,
                error: null,
                isDone: false,
                inProgress: false,
            }));

            const duplicate = normalizedFiles.find((nf) =>
                this.files.some((existing) => existing.path === nf.path)
            );

            if (duplicate) {
                UIkit.notification({
                    message: this.$t('file.already.exists', {
                        name: duplicate.path,
                    }),
                    status: 'error',
                    pos: 'top-center',
                    timeout: this.notificationTimeout,
                });
                return;
            }

            this.files = this.files.concat(normalizedFiles);

            if (!this.inputFields[2].value && files.length) {
                const firstPath =
                    files[0].webkitRelativePath ||
                    files[0]._relativePath ||
                    files[0].name;
                this.inputFields[2].value = firstPath.split('/')[0];
                this.inputFields[2].valid = true;
            }
        },

        actionContinue() {
            this.validationTrigger++;

            if (this.files.length === 0) {
                this.filesError = this.$t('x.required.message', {
                    x: this.$t('files'),
                });
            } else {
                this.filesError = null;
            }

            for (let i = 0, j = this.inputFields.length; i < j; i++) {
                if (!this.inputFields[i].valid) {
                    return;
                }
            }

            if (this.files.length === 0) {
                const id = 'files-label';
                UIkit.scroll('#' + id, {offset: 0}).scrollTo('#' + id);
                return;
            }

            this.verifyTransferToken();
        },
        verifyTransferToken() {
            this.continueLoading = true;

            let http = axios.create();
            http.post(
                this.apiUrl + '/swift/requests/' + this.token + '/verify',
                {
                    email: this.inputFields[0].value,
                    lang: this.$i18n.locale,
                }
            )
                .then(() => {
                    this.showStepEmail = true;

                    this.continueLoading = false;
                    this.continueErr = null;
                })
                .catch((e) => {
                    this.checkGenericError(e);

                    this.continueLoading = false;
                    this.continueErr = e;
                });
        },
        setValid(valid, fieldKey) {
            this.inputFields[fieldKey].valid = valid;
        },
        setValidCode(valid, fieldKey) {
            this.inputFieldsCode[fieldKey].valid = valid;
        },
        actionTriggerInputFiles() {
            document.getElementById(this.inputFormIDFiles).click();
        },
        actionComplete() {
            this.validationTriggerCode++;

            for (let i = 0, j = this.inputFieldsCode.length; i < j; i++) {
                if (!this.inputFieldsCode[i].valid) {
                    return;
                }
            }

            this.completeLoading = true;
            let http = axios.create();
            http.post(
                this.apiUrl + '/swift/requests/' + this.token + '/complete',
                {
                    name: this.inputFields[1].value,
                    title: this.inputFields[2].value,
                    code: this.inputFieldsCode[0].value,
                    lang: this.$i18n.locale,
                }
            )
                .then(() => {
                    this.completeSuccess = true;
                    this.completeErr = null;
                    this.completeLoading = false;
                })
                .catch((e) => {
                    this.checkGenericError(e);

                    this.completeSuccess = false;
                    this.completeErr = e;
                    this.completeLoading = false;
                });
        },
        actionReturn() {
            this.showStepEmail = false;
        },
        actionTriggerInputFolders() {
            document.getElementById(this.inputFormIDFolders).click();
        },
        getTransferToken() {
            if (!this.getInfos.transfer.channelEnabled) {
                this.error = null;
                this.loading = false;
                return;
            }

            let http = axios.create();
            http.post(this.apiUrl + '/swift/requests')
                .then((response) => {
                    this.token = response.data.token;

                    this.error = null;
                    this.loading = false;

                    this.uploadQueue();
                })
                .catch((e) => {
                    this.checkGenericError(e);
                    this.error = e;
                    this.loading = false;
                });
        },
    },
};
</script>

<style lang="scss" scoped>
.word-struct {
    word-wrap: break-word;
}

.transfer-message {
    color: $site-background;
    font-size: 14px;
}

.transfer-struct {
    min-height: calc(100vh - 217px - 24px);
}

@media (min-width: 400px) {
    .transfer-struct {
        max-width: 400px;
        padding-left: 20px;
    }
}

.expires-at-text {
    color: #999;
}

.transfer-report {
    font-size: 12px;
    padding: 15px 0;
    //background-color: #F9F9F9;
    //border-color: #F9F9F9;
}

.delete-message {
    font-size: 14px;
}

.transfer-report-message {
    color: #999;
    font-size: 13px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.logo-box {
    backdrop-filter: saturate(180%) blur(20px);
    background-color: rgba(29, 29, 31, 0.3) !important;
    padding: 10px 20px;
    border-radius: 5px;
    font-weight: 500;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.file-path {
    margin-top: 3px;
    font-size: 16px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
}

.card-struct {
    border-left-width: 3px;
    border-top-width: 3px;
    border-right-width: 3px;
    border-bottom-width: 3px;
    border-bottom-style: solid;
    border-color: #91d800;
    border-radius: 5px;

    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    background-color: #f9f9f9;
}

.transfer-content-struct {
    border-radius: 5px;
    padding-top: 20px;
    // padding-right: 15px;
    // padding-left: 15px;
    background-color: #ffffff;
    padding-bottom: 5px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.01);
    border-bottom-right-radius: 0;
    border-bottom-left-radius: 0;
    color: $site-background;
}

.embedded {
    height: 100%;
}

.transfer-content-struct-panel {
    min-height: 350px;
}

.uk-panel-scrollable {
    height: initial !important;
    overflow: initial !important;
}

@media (min-width: 400px) {
    .transfer-content-struct-panel {
        max-height: calc(100vh - 235px - 40px - 102px);
    }

    .uk-panel-scrollable {
        height: 170px !important;
        overflow: auto !important;
    }
}

.transfer-name {
    font-size: 20px;
    margin-bottom: 15px;
    color: $site-background;
}

.transfer-size {
    font-size: 14px;
    font-weight: 500;
    color: $site-background;
}

.js-upload {
    border-radius: 5px;
}

.uk-panel-scrollable {
    resize: none !important;
    border: none !important;

    padding-right: 15px !important;
    padding-left: 15px !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
}

.uk-card-footer {
    padding-right: 15px !important;
    padding-left: 15px !important;
}

.uk-placeholder {
    margin-top: 0 !important;
}
</style>
